import { inject, Injectable } from '@angular/core';
import { NeoToastService } from '@neogov/pattern-library/neo-toast';

/**
 * ToastProvider
 *
 * Centralized toast notification service for Case Manager.
 * Wraps NeoToastService with consistent configuration and styling.
 *
 * Features:
 * - Bottom-right positioning by default
 * - Custom Case Manager styling via cm-toast class
 * - CSS-based animations (slideUp on appear, slideRight on dismiss)
 */
@Injectable({
  providedIn: 'root',
})
export class ToastProvider {
  private readonly _neoToastService = inject(NeoToastService);

  /**
   * Default toast configuration for Case Manager
   */
  private readonly _defaultConfig = {
    positionClass: 'toast-bottom-right',
    toastClass: 'ngx-toastr cm-toast',
    closeButton: true, // Enable close button (styled as "Dismiss" link)
    tapToDismiss: false, // Disable tap to dismiss - only use Dismiss button
    timeOut: 5000, // Auto-dismiss after 5 seconds
    extendedTimeOut: 2000, // Extended timeout on hover
    easeTime: 300, // Animation duration in ms
    progressBar: false, // Disable progress bar
    enableHtml: false, // Disable HTML in messages
    messageClass: '', // Remove default message class
    titleClass: '', // Remove default title class
    disableTimeOut: false, // Enable timeout
    newestOnTop: false, // Show newest toasts at bottom (for bottom-right positioning)
  };

  /**
   * Show success toast notification
   * @param title - The toast title (bold, larger text)
   * @param message - Optional toast message (smaller text below title)
   */
  public success(title: string, message?: string): void {
    this._neoToastService.success(message || '', title, this._defaultConfig, this._announcement(title, message));
  }

  /**
   * Show error toast notification
   * @param title - The toast title (bold, larger text)
   * @param message - Optional toast message (smaller text below title)
   */
  public error(title: string, message?: string): void {
    this._neoToastService.error(message || '', title, this._defaultConfig, this._announcement(title, message));
  }

  /**
   * Show warning toast notification
   * @param title - The toast title (bold, larger text)
   * @param message - Optional toast message (smaller text below title)
   */
  public warning(title: string, message?: string): void {
    this._neoToastService.warning(message || '', title, this._defaultConfig, this._announcement(title, message));
  }

  /**
   * Show info toast notification
   * @param title - The toast title (bold, larger text)
   * @param message - Optional toast message (smaller text below title)
   */
  public info(title: string, message?: string): void {
    this._neoToastService.info(message || '', title, this._defaultConfig, this._announcement(title, message));
  }

  /**
   * Builds the live-region announcement text for a toast.
   *
   * Every toast type gets one. The visual toast lives in an `aria-live="polite"`
   * container whose updates can be coalesced or dropped, so without an explicit
   * announcement screen-reader users can miss the feedback entirely (WCAG 4.1.3).
   *
   * NeoToastService announces assertively for all four types — it does not expose a
   * politeness option. That is acceptable here because a toast is always the direct
   * response to an action the user just performed, so they are waiting for it.
   */
  private _announcement(title: string, message?: string): string {
    return message ? `${title}. ${message}` : title;
  }
}
